'use client'
import { clsx } from 'clsx'
import * as React from 'react'
import { TooltipContentProps } from '@radix-ui/react-tooltip'

import { Tooltip, TooltipTrigger, TooltipContent } from './tooltip'
import { Button, ButtonProps } from './button'
import { CheckIcon, CopyIcon } from '@radix-ui/react-icons'

export const CopyTooltip = ({
  label,
  sideOffset = 0,
  side,
  className,
  tooltipLabel = { idle: 'Copy', active: 'Copied!', minWidth: '64px' },
  copyContent,
  iconClassName,
}: {
  label?: string | React.ReactNode
  copyContent: string | (() => string | Promise<string>)
  sideOffset?: number
  side?: TooltipContentProps['side']
  iconClassName?: string
  className?: string
  tooltipLabel?: {
    idle: string
    active: string
    minWidth: string | number
  }
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false)
  const [copied, setCopied] = React.useState(0)

  React.useEffect(() => {
    if (!copied) return

    const timeout = setTimeout(() => {
      setCopied(0)
    }, 2000)

    return () => clearTimeout(timeout)
  }, [copied])
  const id = React.useId()

  const onBlur = () => {
    setIsTooltipOpen(false)
  }

  return (
    <Tooltip open={isTooltipOpen} delayDuration={100}>
      <TooltipTrigger asChild>
        <Button
          unstyled
          focusStyled
          onClick={async (e) => {
            e.preventDefault()
            e.stopPropagation()
            const resolvedCopyContent =
              typeof copyContent === 'function'
                ? await copyContent()
                : ((copyContent ?? label) as string)
            navigator.clipboard.writeText(resolvedCopyContent?.toString())
            setCopied((p) => p + 1)
          }}
          aria-label={copied ? tooltipLabel.active : tooltipLabel.idle}
          className={clsx(
            'cursor-default !rounded-sm text-xs leading-none text-dark-faint hover:text-dark-muted',
            className
          )}
          onMouseEnter={() => setIsTooltipOpen(true)}
          onFocus={() => {
            setIsTooltipOpen(true)
          }}
          onBlur={onBlur}
          onMouseLeave={onBlur}
        >
          <span>
            {label ? (
              label
            ) : (
              <CopyIcon className={clsx(iconClassName, 'h-4 w-4')} />
            )}
          </span>
        </Button>
      </TooltipTrigger>
      <TooltipContent
        side={side}
        sideOffset={sideOffset}
        className="inline-block overflow-hidden text-center no-underline"
        style={{ minWidth: tooltipLabel.minWidth }}
        asChild
      >
        <span id={id}>{copied ? tooltipLabel.active : tooltipLabel.idle}</span>
      </TooltipContent>
    </Tooltip>
  )
}

export const CopyButton = ({
  onCopy,
  label = { idle: 'Copy', active: 'Copied!' },
  icon,
  ...props
}: {
  onCopy: () => Promise<void>
  label?: {
    idle: string
    active: string
  }
} & Omit<ButtonProps, 'ref' | 'children' | 'onClick'>) => {
  const [copied, setCopied] = React.useState(0)

  React.useEffect(() => {
    if (!copied) return

    const timeout = setTimeout(() => {
      setCopied(0)
    }, 2000)

    return () => clearTimeout(timeout)
  }, [copied])

  return (
    // @ts-expect-error
    <Button
      size="small"
      intent="primary"
      icon={
        copied ? (
          <CheckIcon height={14} width={14} />
        ) : (
          icon || <CopyIcon height={14} width={14} />
        )
      }
      onClick={async (e) => {
        e.preventDefault()
        e.stopPropagation()
        onCopy().then(() => setCopied((p) => p + 1))
      }}
      {...props}
    >
      {copied ? label.active : label.idle}
    </Button>
  )
}
